import React from 'react'

import moment from 'moment'

import styled from '@emotion/styled'

import QuestionCircleOutlined from '@ant-design/icons/QuestionCircleOutlined'
import { Table, Tooltip, Badge } from 'antd'
import { ColumnsType } from 'antd/lib/table'

import { observer } from 'mobx-react-lite'

import { useVendorStores } from 'vendor/hooks/use-stores'
import { ManagedVendorUser } from 'vendor/server/vendor_settings/manage_users'

import { TableActions } from './table_actions'
import UserInvite from './user_invite'
import UserRole from './user_role'
import UserTier from './user_tier'
import { UserTogglePublic } from './user_toggle_public'

const TableStyled = styled(Table)`
  width: 100%;

  tr.disabled-table-row:hover > td,
  .disabled-table-row {
    background-color: #f5f5f5;
  }
`

const UsersTable = observer(() => {
  const { vendorSettingStore, userStore } = useVendorStores()

  if (vendorSettingStore.users.length === 0) {
    return null
  }

  const columns: ColumnsType<ManagedVendorUser> = [
    {
      title: 'First Name',
      dataIndex: 'first_name',
      key: 'first_name',
      width: 120,
    },
    {
      title: 'Last Name',
      dataIndex: 'last_name',
      key: 'last_name',
      width: 100,
    },
    {
      title: 'Email',
      dataIndex: 'email',
      key: 'email',
      render: (text) => <a href={`mailto:${text}`}>{text}</a>,
    },
    {
      title: 'Joined At',
      dataIndex: 'created_at',
      key: 'created_at',
      width: 170,
      render: (created_at, record) => (
        <UserInvite
          key={record.id}
          created_at={created_at}
          {...record}
          can_manage_users={userStore.canManageUsers}
          resendInvitation={vendorSettingStore.resendInvitation}
        />
      ),
    },
    {
      title: 'Deactivated At',
      dataIndex: 'deactivated_at',
      key: 'deactivated_at',
      width: 170,
      render: (deactivated_at, record) => {
        if (deactivated_at && !record.is_active) {
          return <div>{moment(deactivated_at).format('lll')}</div>
        }
      },
    },
    {
      title: 'Role',
      dataIndex: 'role',
      key: 'role',
      width: 132,
      render: (_text, record) => <UserRole key={record.id} {...record} />,
    },
    {
      title: 'Tier',
      dataIndex: 'tier',
      key: 'tier',
      width: 64,
      render: (_text, record) => <UserTier key={record.id} {...record} />,
    },
    {
      title: (
        <Badge
          count={
            <Tooltip placement="left" title="Should this user be public for new contractors on Subbase to discover?">
              <QuestionCircleOutlined style={{ top: -5, right: -5 }} />
            </Tooltip>
          }
        >
          <span>Public</span>
        </Badge>
      ),
      dataIndex: 'is_public',
      key: 'is_public',
      width: 64,
      render: (isPublic, record) => (
        <UserTogglePublic
          {...record}
          isPublic={isPublic}
          userId={record.id}
          userName={`${record.first_name} ${record.last_name}`}
        />
      ),
    },
    {
      align: 'right',
      key: 'actions',
      width: 64,
      render: (_, record) => <TableActions key={record.id} {...record} />,
    },
  ]

  return (
    <TableStyled
      rowKey={'id'}
      dataSource={[...vendorSettingStore.users]}
      columns={columns}
      pagination={false}
      rowClassName={(record: ManagedVendorUser) => {
        return !record?.is_active ? 'disabled-table-row' : ''
      }}
      scroll={{ x: 1250 }}
    />
  )
})

export default UsersTable
